import { useQuery } from "@tanstack/react-query";

import { getUnitDetail, type UnitDetail } from "@/api/fleet";
import { formatDate } from "@/lib/utils";

interface Props {
  unitId: number;
}

export function SummaryTab({ unitId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["unit-detail", unitId],
    queryFn: () => getUnitDetail(unitId),
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return <div className="text-muted-200">Cargando…</div>;
  }
  if (!data) {
    return <div className="text-muted-200">Sin información.</div>;
  }

  return (
    <div className="space-y-4" data-testid="unit-summary">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="text-xl font-semibold">{data.info.alias}</h2>
          <p className="text-sm text-muted-200">
            {data.info.placa ?? "Sin placa"} ·{" "}
            {data.info.gerencia ?? "Sin gerencia"}
          </p>
        </div>
        <span
          className={
            "ml-auto rounded-full px-2 py-0.5 text-xs " +
            estadoToneClass(data.stateNow.estado)
          }
        >
          {data.stateNow.estado}
        </span>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiWindow title="Hoy" window={data.kpiToday} />
        <KpiWindow title="Últimos 7 días" window={data.kpiLast7d} />
        <KpiWindow title="Últimos 30 días" window={data.kpiLast30d} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <StateCard detail={data} />
        <AlertsCard alerts={data.alerts} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {data.lastTelemetry && <TelemetryCard t={data.lastTelemetry} />}
        {data.lastHit && <LastHitCard hit={data.lastHit} />}
      </div>
    </div>
  );
}

function StateCard({ detail }: { detail: UnitDetail }) {
  return (
    <section className="card">
      <h3 className="text-sm font-medium mb-2">Estado actual</h3>
      <Row label="Operador" value={detail.stateNow.operadorNombre ?? "—"} />
      <Row label="Operativo" value={detail.stateNow.operativoNombre ?? "—"} />
      <Row
        label="Velocidad"
        value={
          detail.stateNow.speedKmh != null
            ? `${Math.round(detail.stateNow.speedKmh)} km/h`
            : "—"
        }
      />
      <Row
        label="Coords"
        value={
          detail.stateNow.latitude != null && detail.stateNow.longitude != null
            ? `${detail.stateNow.latitude.toFixed(5)}, ${detail.stateNow.longitude.toFixed(5)}`
            : "—"
        }
      />
      <Row label="Último latido" value={formatDate(detail.stateNow.lastHeartbeatAt)} />
      {detail.sesionActiva && (
        <div className="mt-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 p-2 text-xs">
          <div className="text-emerald-300 font-medium">Sesión activa</div>
          <div className="text-muted-200">
            {formatDate(detail.sesionActiva.inicio)} —{" "}
            {detail.sesionActiva.minutosActiva} min
          </div>
        </div>
      )}
      {detail.pausaActiva && (
        <div className="mt-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs">
          <div className="text-amber-300 font-medium">
            Pausa abierta {detail.pausaActiva.minutosPausa} min
          </div>
          <div className="text-muted-200">
            {detail.pausaActiva.motivoNombre ?? "Motivo sin nombre"}
          </div>
        </div>
      )}
    </section>
  );
}

function AlertsCard({ alerts }: { alerts: UnitDetail["alerts"] }) {
  if (alerts.length === 0) {
    return (
      <section className="card">
        <h3 className="text-sm font-medium mb-2">Alertas</h3>
        <p className="text-xs text-muted-200">Sin alertas registradas.</p>
      </section>
    );
  }
  return (
    <section className="card">
      <h3 className="text-sm font-medium mb-2">Alertas</h3>
      <ul className="space-y-1 text-sm">
        {alerts.map((a) => (
          <li
            key={a.severidad}
            className="flex items-baseline justify-between gap-2"
          >
            <span className="capitalize text-muted-200">{a.severidad}</span>
            <span>
              {a.abiertas > 0 ? (
                <strong className="text-rose-400">{a.abiertas} abiertas</strong>
              ) : (
                <span className="text-muted-200">0 abiertas</span>
              )}{" "}
              · {a.total24h} en 24 h
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TelemetryCard({ t }: { t: NonNullable<UnitDetail["lastTelemetry"]> }) {
  return (
    <section className="card">
      <h3 className="text-sm font-medium mb-2">Telemetría</h3>
      <Row label="Recibido" value={formatDate(t.at)} />
      <Row label="CPU" value={t.cpuPct != null ? `${t.cpuPct.toFixed(1)} %` : "—"} />
      <Row label="RAM" value={t.ramPct != null ? `${t.ramPct.toFixed(1)} %` : "—"} />
      <Row label="Disco" value={t.discoPct != null ? `${t.discoPct.toFixed(1)} %` : "—"} />
      <Row label="Cámara LPR" value={t.cameraLprStatus ?? "—"} />
      <Row label="GPS" value={t.gpsFix ?? "—"} />
      <Row label="Red" value={t.redTipo ?? "—"} />
    </section>
  );
}

function LastHitCard({ hit }: { hit: NonNullable<UnitDetail["lastHit"]> }) {
  return (
    <section className="card border-rose-500/40">
      <h3 className="text-sm font-medium mb-2">Último hit</h3>
      <Row label="Fase" value={hit.fase} />
      <Row label="Placa" value={hit.placa ?? "—"} />
      <Row label="VIN" value={hit.vin ?? "—"} />
      <Row label="Fecha" value={formatDate(hit.at)} />
    </section>
  );
}

function KpiWindow({
  title,
  window,
}: {
  title: string;
  window: UnitDetail["kpiToday"];
}) {
  return (
    <section className="card">
      <h3 className="text-sm font-medium mb-2">{title}</h3>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <KV label="Lecturas" value={window.readings} />
        <KV label="LPR" value={window.lprEvents} />
        <KV label="Hits" value={window.hits} tone={window.hits > 0 ? "danger" : "n"} />
        <KV label="Reportados" value={window.hitsReportados} />
        <KV label="Descartados" value={window.hitsDescartados} />
        <KV label="Autoridad" value={window.hitsAutoridadNotificada} />
        <KV label="Pausas" value={window.pausasCount} />
        <KV
          label="Tiempo pausa"
          value={formatSeconds(window.pausasSeconds)}
        />
        <KV label="Sesiones" value={window.sesionesCount} />
        <KV
          label="Distancia"
          value={`${window.distanciaKm.toFixed(1)} km`}
        />
        <KV
          label="En movimiento"
          value={formatSeconds(window.tiempoMovimientoSeconds)}
        />
        <KV
          label="Detenida"
          value={formatSeconds(window.tiempoDetenidoSeconds)}
        />
      </div>
    </section>
  );
}

function KV({
  label,
  value,
  tone = "n",
}: {
  label: string;
  value: React.ReactNode;
  tone?: "n" | "danger";
}) {
  const cls = tone === "danger" ? "text-rose-400" : "text-foreground";
  return (
    <div className="flex items-baseline justify-between gap-1 text-xs">
      <span className="text-muted-200">{label}</span>
      <span className={`font-medium ${cls}`}>{value}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="text-xs text-muted-200">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function estadoToneClass(estado: string): string {
  switch (estado) {
    case "hit_activo":
      return "bg-rose-500 text-white";
    case "patrullando":
    case "en_operativo":
      return "bg-emerald-500 text-white";
    case "en_pausa":
    case "detenido":
      return "bg-amber-500 text-zinc-900";
    case "offline":
      return "bg-zinc-500 text-white";
    default:
      return "bg-zinc-700 text-white";
  }
}

function formatSeconds(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
